import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.thelemonadelivingcompany.com";
const SITE_NAME = "The Lemonade Living Company";
const DEFAULT_DESC = "Burnout recovery & mental wellbeing for women professionals. Free ebooks, courses, wellness kits & journals. South Africa.";
const DEFAULT_IMG = "https://static.prod-images.emergentagent.com/jobs/57407f6c-76a2-4eb0-9f90-849104b8ea6c/images/b5ae1db51302788692ed3cd42391fd6d196a96000ab190cf19073c6da1b129af.png";

// Organization Schema (appears on every page)
const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  description: "A South African mental wellbeing and burnout recovery company helping professionals — especially women in STEM — find balance, resilience, and joy through evidence-informed courses, wellness kits, and free resources.",
  foundingDate: "2025",
  areaServed: { "@type": "Country", name: "South Africa" },
  sameAs: [],
};

// LocalBusiness + MedicalBusiness Schema (homepage only)
const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "MedicalBusiness"],
  "@id": `${SITE_URL}/#business`,
  name: SITE_NAME,
  description: "South African mental wellbeing and burnout recovery company offering evidence-informed courses, wellness kits, digital journals, and free resources for women professionals.",
  url: SITE_URL,
  email: "lemonadeliving4me@gmail.com",
  image: DEFAULT_IMG,
  priceRange: "R0 - R2,499",
  address: {
    "@type": "PostalAddress",
    addressCountry: "ZA",
    addressRegion: "South Africa",
  },
  areaServed: {
    "@type": "Country",
    name: "South Africa",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "00:00",
    closes: "23:59",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Mental Wellbeing Products",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Burnout To Balance Ebook", description: "Free 8-chapter burnout recovery guide" } },
      { "@type": "Offer", itemOffered: { "@type": "Course", name: "Burnout To Balance In Action Course", description: "10-lesson self-paced burnout recovery course with video lessons and EQ exercises" } },
      { "@type": "Offer", itemOffered: { "@type": "Product", name: "Wellness Kits", description: "Self-Care, Meditation, and Stress Relief digital toolkit bundles" } },
    ],
  },
};

export const PageSEO = ({ title, description, path = "", image, type = "website", children }) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Mental Wellbeing & Wellness`;
  const desc = description || DEFAULT_DESC;
  const url = `${SITE_URL}${path}`;
  const img = image || DEFAULT_IMG;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_ZA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* GEO signals */}
      <meta name="geo.region" content="ZA" />
      <meta name="geo.placename" content="South Africa" />

      {/* Organization Schema */}
      <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>

      {children}
    </Helmet>
  );
};

export const ProductListSchema = ({ products }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Wellness Products — The Lemonade Living Company",
    description: "Burnout recovery and mental wellness products: free ebooks, online courses, and curated wellness kits.",
    itemListElement: products.filter(p => p.type !== "ebook").map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        "@id": `${SITE_URL}/#product-${p.id}`,
        name: p.name,
        description: p.description,
        offers: {
          "@type": "Offer",
          price: p.price,
          priceCurrency: "ZAR",
          availability: "https://schema.org/InStock",
        },
      },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export const ArticleSchema = ({ post }) => {
  if (!post) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${SITE_URL}/blog/${post.id}`,
    headline: post.title,
    description: post.seo?.meta_description || post.excerpt,
    author: { "@type": "Organization", name: SITE_NAME, "@id": `${SITE_URL}/#organization` },
    publisher: { "@type": "Organization", name: SITE_NAME, "@id": `${SITE_URL}/#organization` },
    datePublished: post.date,
    mainEntityOfPage: `${SITE_URL}/blog/${post.id}`,
    keywords: post.seo?.keywords?.join(", ") || post.category,
    inLanguage: "en-ZA",
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export const CourseSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${SITE_URL}/course`,
    name: "Burnout - To Balance In Action",
    description: "A 10-lesson self-paced course with video lessons, interactive quizzes, and emotional intelligence exercises designed to help professionals recover from burnout.",
    provider: { "@type": "Organization", name: SITE_NAME, "@id": `${SITE_URL}/#organization` },
    offers: { "@type": "Offer", price: "2499", priceCurrency: "ZAR", availability: "https://schema.org/InStock" },
    hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: "PT2H" },
    numberOfCredits: 10,
    educationalLevel: "Beginner",
    teaches: "Burnout recovery, emotional intelligence, self-care, resilience, mindfulness, work-life balance",
    inLanguage: "en-ZA",
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export const HOMEPAGE_FAQS = [
  { q: "What is The Lemonade Living Company?", a: "The Lemonade Living Company is a South African mental wellbeing and wellness company that helps professionals — especially women in STEM — recover from burnout and build sustainable balance through courses, wellness kits, and curated resources." },
  { q: "What is the Burnout To Balance ebook about?", a: "Burnout To Balance is a free comprehensive guide covering the signs of burnout, recovery strategies, self-assessment tools, and mindfulness exercises across 8 in-depth chapters. Download it by providing your email address." },
  { q: "How much does the Burnout - To Balance In Action course cost?", a: "The course is R2,499 for a one-time purchase. It includes 10 video lessons, interactive quizzes, emotional intelligence exercises, and a downloadable journal. Ebook readers get an exclusive discount of R1,999." },
  { q: "What are the wellness kits and how much do they cost?", a: "We offer three wellness kits at R250 each: the Self-Care Kit (daily rituals and habits), Meditation Kit (guided meditations and breathing exercises), and Stress Relief Kit (stress management and grounding tools). A bundle of all three is available at R599, saving R151." },
  { q: "How do I access the course after purchasing?", a: "After purchasing via PayFast, visit the Course page and enter the email you used during checkout. Your 10 video lessons, quizzes, and exercises will be unlocked immediately." },
  { q: "Can I export my course exercise responses?", a: "Yes. Once you complete the course, you can download all your exercise responses, journal entries, and quiz scores as a text file for personal review and ongoing reflection." },
];

export const MEDICAL_FAQS = [
  { q: "Is burnout officially recognised as a medical condition?", a: "The World Health Organisation (WHO) classifies burnout as an 'occupational phenomenon' in ICD-11. It is defined as a syndrome resulting from chronic workplace stress that has not been successfully managed, characterised by three dimensions: energy depletion or exhaustion, increased mental distance or cynicism related to one's job, and reduced professional efficacy." },
  { q: "What are the physical symptoms of burnout?", a: "Burnout can manifest with significant physical symptoms including chronic fatigue, insomnia or disrupted sleep patterns, frequent headaches or migraines, gastrointestinal problems, weakened immune system leading to frequent illness, muscle tension and pain, and changes in appetite. These symptoms arise from prolonged activation of the body's stress response system and elevated cortisol levels." },
  { q: "When should I seek professional help for burnout?", a: "Consult a healthcare professional if you experience persistent feelings of hopelessness, if burnout symptoms interfere with daily functioning, if you notice signs of clinical depression or anxiety, if you're using substances to cope, or if physical symptoms like chest pain, severe insomnia, or panic attacks develop. Our resources complement — but do not replace — professional medical or psychological care." },
  { q: "Can burnout lead to depression or anxiety disorders?", a: "Yes. Research published in the Journal of Occupational Health Psychology shows that chronic burnout significantly increases the risk of developing clinical depression, generalised anxiety disorder, and other mental health conditions. Prolonged stress dysregulates the hypothalamic-pituitary-adrenal (HPA) axis and can cause lasting changes in brain chemistry. Early intervention is crucial." },
  { q: "How long does burnout recovery typically take?", a: "Recovery timelines vary depending on severity and duration. Mild burnout may improve within weeks to months with proper self-care and boundary-setting. Severe burnout can take 6 months to 2 years of consistent effort, which may include therapy, lifestyle changes, and potentially time off work. Our programmes support each stage of recovery with evidence-informed tools and practices." },
  { q: "Are your resources a substitute for therapy or medical treatment?", a: "No. Our ebooks, courses, wellness kits, and toolkit exercises are educational and self-help resources designed to support mental wellbeing. They are not a substitute for professional medical advice, diagnosis, or treatment. If you are experiencing a mental health crisis, please contact the South African Depression and Anxiety Group (SADAG) on 0800 567 567 or your local emergency services." },
];

export const LocalBusinessSchema = () => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(LOCAL_BUSINESS_SCHEMA)}</script>
  </Helmet>
);

export const COURSE_FAQS = [
  { q: "What format is the course in?", a: "The course consists of 10 video lessons hosted on YouTube (unlisted), each followed by an interactive quiz and an emotional intelligence exercise. You complete it at your own pace." },
  { q: "Do I get a certificate?", a: "You receive a completion badge and a full progress report with your quiz scores and exercise responses, which you can export as a journal." },
  { q: "Is there a refund policy?", a: "Please contact us at lemonadeliving4me@gmail.com within 7 days of purchase if you're not satisfied. We'll work with you to resolve any concerns." },
  { q: "What makes this course different from free burnout content online?", a: "This course combines structured video learning with interactive quizzes that test understanding, and unique emotional intelligence exercises like burnout self-assessments, breathing techniques, journaling prompts, and support circle mapping — all with personalised progress tracking." },
];
